import { useEffect, useMemo, useState } from "react";
import { useSearch, useLocation } from "wouter";
import { useGetBvaComparison, getGetBvaComparisonQueryKey, useGetBvaTransactionEvidence, getGetBvaTransactionEvidenceQueryKey } from "@workspace/api-client-react";
import type { BvaAccountComparison } from "@workspace/api-client-react";
import { Shell, PageHead, Tag, formatMoney, formatVariance, formatPercent, formatPeriod } from "@/components/tekledger-ui";
import { useWorkspaceCompany } from "@/hooks/use-workspace-company";
import { Search, AlertCircle, Loader2, ArrowRight, Check, ChevronRight } from "lucide-react";

export default function Variance() {
  const { companyId } = useWorkspaceCompany();
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const params = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const fyParam = params.get("fy");
  const periodParam = params.get("period");
  const period = periodParam?.match(/^(?:\d{4}-)?(0[1-9]|1[0-2])$/)?.[1] ?? "07";
  const department = params.get("department")?.trim() || "643";

  const fy = fyParam ? Number(fyParam) : 2026;

  useEffect(() => {
    const hasValidContext = Boolean(
      fyParam
      && periodParam?.match(/^(?:\d{4}-)?(0[1-9]|1[0-2])$/)
      && params.get("department")?.trim(),
    );
    if (hasValidContext) return;
    const next = new URLSearchParams(params);
    next.set("fy", String(fy));
    next.set("period", period);
    next.set("department", department);
    setLocation(`/variance?${next.toString()}`, { replace: true });
  }, [department, fy, fyParam, params, period, periodParam, setLocation]);

  const [periodView, setPeriodView] = useState<"current_month" | "ytd">("current_month");
  const { data: bva, isLoading, isError, error } = useGetBvaComparison({
    companyId,
    fiscalYear: fy,
    throughPeriod: period || undefined,
    departmentCode: department || undefined,
    periodView,
  }, {
    query: {
      enabled: !!fy && !!period && !!department,
      queryKey: getGetBvaComparisonQueryKey({ companyId, fiscalYear: fy, throughPeriod: period || undefined, departmentCode: department || undefined, periodView })
    }
  });

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all, material, unfavorable, favorable, actual_only, budget_only
  const [materialThreshold, setMaterialThreshold] = useState(25000);
  const [materialPercent, setMaterialPercent] = useState(10);
  const [showMaterialityConfig, setShowMaterialityConfig] = useState(false);
  const [selectedGl, setSelectedGl] = useState<string | null>(null);
  const [selectedForExplain, setSelectedForExplain] = useState<Set<string>>(new Set());
  const [inspectedGls, setInspectedGls] = useState<Set<string>>(new Set());
  const [showTransactions, setShowTransactions] = useState(false);
  const [showHandoffDialog, setShowHandoffDialog] = useState(false);
  useEffect(() => {
    setSelectedGl(null);
    setSelectedForExplain(new Set());
    setInspectedGls(new Set());
    setShowTransactions(false);
    setShowHandoffDialog(false);
  }, [periodView]);
  const evidenceParams = {
    companyId,
    fiscalYear: fy,
    throughPeriod: period ?? "",
    departmentCode: department ?? "",
    glAccount: selectedGl ?? "",
    periodView,
  };
  const { data: transactionEvidence, isLoading: isLoadingTransactions, isError: isTransactionError } = useGetBvaTransactionEvidence(evidenceParams, {
    query: {
      enabled: Boolean(period && department && selectedGl && showTransactions),
      queryKey: getGetBvaTransactionEvidenceQueryKey(evidenceParams),
    },
  });

  if (isLoading) {
    return (
      <Shell active="Variance">
        <section className="tl-content">
          <div className="bva-loading" style={{ minHeight: 300 }}>
            <Loader2 className="animate-spin" size={24} /> Loading variance details...
          </div>
        </section>
      </Shell>
    );
  }

  if (isError || !bva) {
    return (
      <Shell active="Variance">
        <section className="tl-content">
          <div className="source-page-error">
            <AlertCircle size={15} />
            <div><b>Error loading comparison</b><span>{error?.message || "Data unavailable"}</span></div>
          </div>
        </section>
      </Shell>
    );
  }

  const accounts = (bva.accountCoverage.accounts || []).filter((account) => account.budget !== 0 || account.actual !== 0);
  
  // Logic
  const isMaterial = (acc: BvaAccountComparison) => {
    if (acc.status === "actual_only" || acc.status === "budget_only") return true;
    const absVar = Math.abs(acc.variance);
    if (absVar >= materialThreshold) return true;
    
    // Variance percentage logic
    if (acc.budget && acc.budget !== 0) {
      const pct = Math.abs(acc.variance / acc.budget) * 100;
      if (pct >= materialPercent) return true;
    }
    return false;
  };

  const filteredAccounts = accounts.filter((acc) => {
    // Search
    if (query) {
      const search = query.toLowerCase();
      if (!acc.glAccount.toLowerCase().includes(search) && !(acc.accountName?.toLowerCase() || "").includes(search)) {
        return false;
      }
    }
    // Quick filters
    if (filter === "material" && !isMaterial(acc)) return false;
    if (filter === "unfavorable" && acc.variance <= 0) return false;
    if (filter === "favorable" && acc.variance >= 0) return false;
    if (filter === "actual_only" && acc.status !== "actual_only") return false;
    if (filter === "budget_only" && acc.status !== "budget_only") return false;
    return true;
  });

  const materialAccounts = accounts.filter(isMaterial);
  const materialCount = materialAccounts.length;
  const inspectedMaterialCount = materialAccounts.filter(a => inspectedGls.has(a.glAccount)).length;
  const remainingMaterialCount = materialAccounts.filter(account =>
    !inspectedGls.has(account.glAccount) && !selectedForExplain.has(account.glAccount)
  ).length;
  const selectedAbsoluteVariance = accounts
    .filter(account => selectedForExplain.has(account.glAccount))
    .reduce((sum, account) => sum + Math.abs(account.variance), 0);

  const exceptionActualOnly = accounts.filter(a => a.status === "actual_only");
  const exceptionBudgetOnly = accounts.filter(a => a.status === "budget_only");

  const monthlyBva = bva.monthly.find(m => m.period.endsWith(`-${period}`));

  const analyzeAccount = (gl: string) => {
    setSelectedGl(gl);
    setShowTransactions(false);
    setInspectedGls(prev => new Set(prev).add(gl));
  };

  const selectedAccount = accounts.find(a => a.glAccount === selectedGl);

  const toggleForExplain = (gl: string) => {
    setSelectedForExplain(previous => {
      const next = new Set(previous);
      if (next.has(gl)) next.delete(gl);
      else next.add(gl);
      return next;
    });
  };

  const selectAccounts = (items: BvaAccountComparison[]) => {
    setSelectedForExplain(new Set(items.map(item => item.glAccount)));
  };

  const continueToExplain = (accountsToExplain = Array.from(selectedForExplain)) => {
    if (accountsToExplain.length === 0) {
      if (materialAccounts.length > 0) {
        setShowHandoffDialog(true);
        return;
      }
    }
    setShowHandoffDialog(false);
    const next = new URLSearchParams(searchString);
    next.delete("gl");
    next.set("gls", accountsToExplain.join(","));
    next.set("mode", accountsToExplain.length > 1 ? "group" : accountsToExplain.length === 1 ? "single" : "empty");
    next.set("view", periodView);
    next.set("workload", materialAccounts.length === 0
      ? "empty"
      : accountsToExplain.length === materialAccounts.length && materialAccounts.every(account => accountsToExplain.includes(account.glAccount))
        ? "all_material"
        : "selected");
    next.set("materiality", JSON.stringify({ amount: materialThreshold, percent: materialPercent }));
    next.set("records", JSON.stringify(accounts
      .filter(account => accountsToExplain.includes(account.glAccount))
      .map(account => ({
        ...account,
        companyId,
        fiscalYear: fy,
        period,
        departmentCode: department,
        periodView,
        budgetSourceId: bva.basis.budgetSourceId,
        budgetVersion: bva.basis.budgetVersion,
        actualsSourceIds: bva.basis.actualsSourceIds,
        actualsCertificationIds: bva.basis.actualsCertificationIds,
      }))));
    setLocation(`/explain?${next.toString()}`);
  };

  const openExplainFromNav = () => continueToExplain();

  return (
      <Shell active="Variance" onExplain={openExplainFromNav}>
      <section className="tl-content variance-page">
        {/* Top Header */}
        <div className="variance-header">
          <div className="vh-main">
            <div className="eyebrow">Variance Analysis</div>
            <h1>FY{fy} | {formatPeriod(`${fy}-${period}`)} | {department} {bva.scope.departmentName || ""}</h1>
          </div>
          <div className="vh-workflow">
            <div className="wf-step active"><span className="wf-dot" /> Variance</div>
            <button type="button" className="wf-step wf-step-action" onClick={openExplainFromNav}><span className="wf-dot empty" /> Explain</button>
            <div className="wf-step"><span className="wf-dot empty" /> Forecast</div>
            <div className="wf-step"><span className="wf-dot empty" /> Review</div>
          </div>
        </div>

        <div className="variance-view-toggle" aria-label="Variance period view">
          <span className="mini">View:</span>
          <button className={periodView === "current_month" ? "active" : ""} onClick={() => setPeriodView("current_month")}>{formatPeriod(`${fy}-${period}`)}</button>
          <button className={periodView === "ytd" ? "active" : ""} onClick={() => setPeriodView("ytd")}>YTD through {formatPeriod(`${fy}-${period}`)}</button>
        </div>

        {/* Top Summary */}
        <div className="variance-summary-bar">
          <div className="vs-metric">
            <label>{periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : "YTD"} Budget</label>
            <strong>{formatMoney(periodView === "current_month" ? monthlyBva?.budget : bva.kpis.ytdBudget)}</strong>
          </div>
          <div className="vs-metric">
            <label>{periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : "YTD"} Actual</label>
            <strong>{formatMoney(periodView === "current_month" ? monthlyBva?.actual : bva.kpis.ytdActual)}</strong>
          </div>
          <div className="vs-metric">
            <label>Variance</label>
            <strong className={(periodView === "current_month" ? monthlyBva?.variance : bva.kpis.ytdVariance) && (periodView === "current_month" ? monthlyBva?.variance : bva.kpis.ytdVariance)! > 0 ? "bad-text" : "good-text"}>
              {formatVariance(periodView === "current_month" ? monthlyBva?.variance : bva.kpis.ytdVariance)}
            </strong>
          </div>
          <div className="vs-metric">
            <label>Var %</label>
            <strong>{formatPercent(periodView === "current_month" ? monthlyBva?.variancePercent : bva.kpis.ytdVariancePercent)}</strong>
          </div>
          <div className="vs-workload">
            <Tag tone={remainingMaterialCount > 0 ? "bad" : "good"}>
              {remainingMaterialCount > 0
                ? `${accounts.length} items in scope · ${remainingMaterialCount} material require analysis`
                : `${accounts.length} items in scope · All material items analyzed`}
            </Tag>
          </div>
        </div>

        {/* Exceptions */}
        {(exceptionActualOnly.length > 0 || exceptionBudgetOnly.length > 0) && (
          <div className="card bva-exceptions">
            <div className="bva-exception-heading">
              <div>
                <h3>Account Exceptions · {periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : `YTD through ${formatPeriod(`${fy}-${period}`)}`}</h3>
                <span className="mini">Activity outside matched Budget expectations for this view</span>
              </div>
              <button className="tl-btn" onClick={() => setFilter(exceptionActualOnly.length > 0 ? "actual_only" : "budget_only")}>Review Exceptions</button>
            </div>
            <div className="exceptions-grid">
              <div className="exc-item">
                <label>Actual without Budget</label>
                <div><b>{exceptionActualOnly.length}</b><span>{formatMoney(exceptionActualOnly.reduce((sum, a) => sum + a.actual, 0))}</span></div>
              </div>
              <div className="exc-item">
                <label>Budget without Activity</label>
                <div><b>{exceptionBudgetOnly.length}</b><span>{formatMoney(exceptionBudgetOnly.reduce((sum, a) => sum + a.budget, 0))}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* Main Workspace */}
        <div className="variance-workspace">
          <div className="vw-main">
            <div className="vw-toolbar">
              <div className="vw-filters">
                <button className={`method ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All</button>
                <button className={`method ${filter === "material" ? "active" : ""}`} onClick={() => setFilter("material")}>Material</button>
                <button className={`method ${filter === "unfavorable" ? "active" : ""}`} onClick={() => setFilter("unfavorable")}>Unfavorable</button>
                <button className={`method ${filter === "favorable" ? "active" : ""}`} onClick={() => setFilter("favorable")}>Favorable</button>
                <button className={`method ${filter === "actual_only" ? "active" : ""}`} onClick={() => setFilter("actual_only")}>Actual Without Budget</button>
                <button className={`method ${filter === "budget_only" ? "active" : ""}`} onClick={() => setFilter("budget_only")}>Budget Without Actual</button>
              </div>
              <div className="vw-controls">
                <div style={{ position: "relative" }}>
                  <Search size={13} style={{ position: "absolute", left: 9, top: 9, color: "#84918b" }} />
                  <input style={{ paddingLeft: 28, paddingRight: 10, width: 220 }} placeholder="Search GL account..." value={query} onChange={e => setQuery(e.target.value)} />
                </div>
                <div className="vw-grain">
                  <span className="mini">Analyze by:</span>
                  <select defaultValue="gl">
                    <option value="gl">GL Account</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vw-info-bar" style={{ flexWrap: 'wrap' }}>
              <span className="mini">{accounts.length} GL accounts compared for {periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : `YTD through ${formatPeriod(`${fy}-${period}`)}`} · {materialCount} material variances · {accounts.filter(a => a.variance > 0).length} unfavorable · {accounts.filter(a => a.variance < 0).length} favorable</span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span className="mini" style={{display: 'flex', alignItems: 'center', gap: 6}}>
                  Session materiality: &ge;{formatMoney(materialThreshold, "USD", true)} or &ge;{materialPercent}%
                  <button className="tl-fy-action" style={{marginLeft: 4, padding: 0}} onClick={() => setShowMaterialityConfig(!showMaterialityConfig)}>
                    Change
                  </button>
                </span>
                {showMaterialityConfig && (
                  <div style={{display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, background: '#f4f6ef', padding: '8px 12px', borderRadius: 8}}>
                    <label className="mini" style={{display: 'flex', alignItems: 'center'}}>Amount &ge; <input type="number" value={materialThreshold} onChange={e => setMaterialThreshold(Number(e.target.value))} style={{width: 90, padding: '4px 6px', marginLeft: 4}} /></label>
                    <label className="mini" style={{display: 'flex', alignItems: 'center'}}>Percent &ge; <input type="number" value={materialPercent} onChange={e => setMaterialPercent(Number(e.target.value))} style={{width: 60, padding: '4px 6px', marginLeft: 4}} /></label>
                  </div>
                )}
              </div>
            </div>

            <div className="variance-group-bar">
              <b>Select for explanation</b>
              <div className="variance-group-actions">
                <button className="tl-btn" onClick={() => selectAccounts(materialAccounts)}>Select material</button>
                <button className="tl-btn" onClick={() => selectAccounts(materialAccounts.filter(account => account.variance > 0))}>Select unfavorable</button>
                <button className="tl-btn" onClick={() => selectAccounts(exceptionActualOnly)}>Select no-budget</button>
                <button className="tl-btn" onClick={() => selectAccounts(filteredAccounts)}>Select visible</button>
              </div>
              <div className="variance-selection-summary">
                <span><b>{selectedForExplain.size} selected</b>{selectedForExplain.size > 0 ? <> · {formatMoney(selectedAbsoluteVariance)} combined absolute variance</> : <> · Select specific items, or continue with all material variances.</>}</span>
                {selectedForExplain.size > 0 && <button className="tl-fy-action" onClick={() => setSelectedForExplain(new Set())}>Clear</button>}
                <button className="tl-btn primary" onClick={() => continueToExplain()}>
                  {selectedForExplain.size > 0 ? `Continue to Explain · ${selectedForExplain.size} Items` : "Continue to Explain"} <ArrowRight size={13} />
                </button>
              </div>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap" style={{ margin: 0 }}>
                <table className="tl-fy-table">
                  <thead>
                    <tr>
                      <th>Account / Spend</th>
                      <th>{periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : "YTD"} Budget</th>
                      <th>{periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : "YTD"} Actual</th>
                      <th>{periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : "YTD"} Variance</th>
                      <th>Var %</th>
                      <th>Flag</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAccounts.map((r) => {
                      const mat = isMaterial(r);
                      const isSelected = selectedGl === r.glAccount;
                      const hasInspected = inspectedGls.has(r.glAccount);
                      
                      return (
                        <tr key={r.glAccount} onClick={() => analyzeAccount(r.glAccount)} className={isSelected ? "current" : ""} style={{ cursor: 'pointer' }}>
                          <td>
                            <div className="variance-account-cell">
                              <input
                                type="checkbox"
                                checked={selectedForExplain.has(r.glAccount)}
                                onChange={() => toggleForExplain(r.glAccount)}
                                onClick={event => event.stopPropagation()}
                                aria-label={`Select ${r.glAccount} for group explanation`}
                              />
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <b>{r.glAccount}</b>
                                <span className="mini">{r.accountName || "Unmapped"}</span>
                              </div>
                            </div>
                          </td>
                          <td>{formatMoney(r.budget)}</td>
                          <td>{formatMoney(r.actual)}</td>
                          <td className={r.variance > 0 ? "bad-text" : r.variance < 0 ? "good-text" : ""}>
                            {formatVariance(r.variance)}
                          </td>
                          <td>
                            {r.budget === 0 ? "—" : formatPercent(r.variance / r.budget * 100)}
                          </td>
                          <td>
                            {r.status === "actual_only" ? (
                              <Tag tone="bad">No Budget</Tag>
                            ) : r.status === "budget_only" ? (
                              <Tag tone="bad">No Activity</Tag>
                            ) : r.status === "mapping_required" ? (
                              <Tag tone="bad">Mapping Required</Tag>
                            ) : mat ? (
                              <Tag tone="bad">Material</Tag>
                            ) : (
                              <Tag>Small</Tag>
                            )}
                          </td>
                          <td>
                            <button className={`tl-fy-action ${mat && !hasInspected ? "primary" : ""}`}>
                              {mat && !hasInspected ? "Analyze" : "View"}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                    {filteredAccounts.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '30px' }} className="mini">
                          No variances match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="vw-side">
            {selectedAccount ? (
              <div className="card sticky-panel">
                <div className="card-head" style={{ marginBottom: 16 }}>
                  <div>
                    <div className="eyebrow">Selected Item</div>
                    <h3 style={{ fontSize: 16, marginBottom: 2 }}>{selectedAccount.glAccount}</h3>
                    <span className="mini">{selectedAccount.accountName || "Unmapped account"}</span>
                  </div>
                  {isMaterial(selectedAccount) && <Tag tone="bad">Material</Tag>}
                </div>

                <div className="sp-metrics">
                  <div className="sp-metric">
                    <span>Budget</span>
                    <b>{formatMoney(selectedAccount.budget)}</b>
                  </div>
                  <div className="sp-metric">
                    <span>Actual</span>
                    <b>{formatMoney(selectedAccount.actual)}</b>
                  </div>
                  <div className="sp-metric">
                    <span>Variance</span>
                    <b className={selectedAccount.variance > 0 ? "bad-text" : selectedAccount.variance < 0 ? "good-text" : ""}>
                      {formatVariance(selectedAccount.variance)}
                    </b>
                  </div>
                  <div className="sp-metric">
                    <span>Var %</span>
                    <b>{selectedAccount.budget === 0 ? "—" : formatPercent(selectedAccount.variance / selectedAccount.budget * 100)}</b>
                  </div>
                </div>

                <div className="sp-section">
                  <h4>Budget basis</h4>
                  <div className="sp-row"><span>{periodView === "current_month" ? formatPeriod(`${fy}-${period}`) : `YTD through ${formatPeriod(`${fy}-${period}`)}`} Budget</span><b>{formatMoney(selectedAccount.budget)}</b></div>
                  <div className="sp-row"><span>Source status</span><b>{bva.coverage.departments.find(d => d.departmentCode === department)?.budgetStatus === "published" ? "Published" : "Unknown"}</b></div>
                </div>

                <div className="sp-section">
                  <h4>Actual evidence</h4>
                  <div className="sp-row"><span>Source status</span><b>{bva.coverage.departments.find(d => d.departmentCode === department)?.actualsStatus === "certified" ? "Certified" : "Unknown"}</b></div>
                  <div className="sp-row"><span>Matched status</span><b>{selectedAccount.status === "matched" ? "Matched" : selectedAccount.status === "actual_only" ? "Actual Without Budget" : selectedAccount.status === "budget_only" ? "Budget Without Actual Activity" : "Mapping Required"}</b></div>
                </div>

                <button className="tl-btn" style={{ width: "100%", justifyContent: "space-between", marginTop: 10 }} disabled={!selectedAccount.glAccount} onClick={() => setShowTransactions(value => !value)}>
                  {showTransactions ? "Hide Transactions" : "View Transactions"} <ChevronRight size={14} />
                </button>
                {!selectedAccount.glAccount && <div className="mini" style={{ textAlign: 'center', marginTop: 6, opacity: 0.7 }}>Map this account before drilling into transactions.</div>}
                {showTransactions && selectedAccount.glAccount && (
                  <div className="transaction-evidence" data-testid="transaction-evidence">
                    {isLoadingTransactions && <div className="mini"><Loader2 size={12} className="animate-spin" /> Loading certified transactions…</div>}
                    {isTransactionError && <div className="mini bad-text">Certified transaction evidence could not be loaded.</div>}
                    {transactionEvidence && (
                      <>
                        <div className="sp-row"><span>Certified transactions</span><b>{transactionEvidence.transactionCount}</b></div>
                        <div className="sp-row"><span>{periodView === "current_month" ? `${formatPeriod(`${fy}-${period}`)} total` : "Total through close"}</span><b>{formatMoney(transactionEvidence.total, transactionEvidence.currency)}</b></div>
                        <div className="transaction-list">
                          {transactionEvidence.transactions.map((transaction, index) => (
                            <div className="transaction-row" key={`${transaction.period}-${index}`}>
                              <span>{transaction.periodLabel}</span>
                              <p>{transaction.description || "No description provided"}</p>
                              <b>{formatMoney(transaction.amount, transactionEvidence.currency)}</b>
                            </div>
                          ))}
                          {transactionEvidence.transactions.length === 0 && <div className="mini">No certified transactions found for this account.</div>}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="sp-action">
                  {selectedForExplain.has(selectedAccount.glAccount) ? (
                    <div className="selected-for-explain"><b><Check size={13} /> Selected for Explain</b><button className="tl-fy-action" onClick={() => toggleForExplain(selectedAccount.glAccount)}>Remove</button></div>
                  ) : (
                    <button className="tl-btn primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => toggleForExplain(selectedAccount.glAccount)}>
                      Select for Explain
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="card sticky-panel empty-panel">
                <Search size={24} style={{ color: "#a4b4ae", marginBottom: 12 }} />
                <h3>Select an item</h3>
                <p className="mini">Click any row in the variance table to inspect budget basis and actual evidence.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Progress */}
        <div className="variance-footer">
          <div className="vf-progress">
            <div className="mini">Variance Progress</div>
            <div className="variance-progress-line">
              <b>{materialCount} material</b>
              <span>|</span>
              <span>{inspectedMaterialCount} inspected</span>
              <span>|</span>
              <span>{selectedForExplain.size} selected for Explain</span>
              <span>|</span>
              <span>{remainingMaterialCount} remaining</span>
            </div>
          </div>
          <div className="variance-footer-cta">
            {selectedForExplain.size === 0 && <span className="mini">Select specific items, or continue with all material variances.</span>}
            <button className="tl-btn primary" onClick={() => continueToExplain()}>
              {selectedForExplain.size > 0 ? `Continue to Explain · ${selectedForExplain.size} Items` : "Continue to Explain"} <ArrowRight size={14} />
            </button>
          </div>
        </div>
        {showHandoffDialog && (
          <div className="handoff-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setShowHandoffDialog(false); }}>
            <div className="handoff-sheet" role="dialog" aria-modal="true" aria-labelledby="handoff-title">
              <div className="card-head">
                <div>
                  <div className="eyebrow">Workflow handoff</div>
                  <h3 id="handoff-title">Continue to Explain</h3>
                </div>
                <button className="tl-fy-action" onClick={() => setShowHandoffDialog(false)} aria-label="Close handoff dialog">Close</button>
              </div>
              <p className="handoff-lead">No variances have been selected.</p>
              <p className="sub">{materialCount} material variances currently require explanation.</p>
              <div className="handoff-actions">
                <button className="tl-btn primary" onClick={() => continueToExplain(materialAccounts.map(account => account.glAccount))}>Explain All Material Variances <ArrowRight size={14} /></button>
                <button className="tl-btn" onClick={() => setShowHandoffDialog(false)}>Return and Select Specific Variances</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Shell>
  );
}
